import { DateRangeFilterField } from '@/shared/ui';
import type { UserType } from '@/shared/utils/get-group';
import { X } from 'lucide-react';
import type { FilterOption } from '../../hooks/use-reports-filter-options.hook';
import { Field, FilterCombobox } from '../shared/filter-combobox.component';

export interface InstallmentsFilterValues {
  periodFrom: string;
  periodTo: string;
  /** Tipo de período: '1' Emissão / '2' Vencimento. */
  periodType: string;
  advisoryPartyCode: string;
  brokerPartyCode: string;
  productCode: string;
  branchCode: string;
  /** Situação da parcela (dv_situacao): C / P / Q. */
  installmentStatus: string;
}

interface FiltersProps {
  /** Painel aberto/fechado (controla a animação de abrir/fechar). */
  open: boolean;
  /** Algum filtro do painel está diferente do padrão (controla o "Limpar Seleção"). */
  hasActiveFilter: boolean;
  userType: UserType | null;
  values: InstallmentsFilterValues;
  advisoryOptions: FilterOption[];
  brokerOptions: FilterOption[];
  productOptions: FilterOption[];
  branchOptions: FilterOption[];
  installmentStatusOptions: FilterOption[];
  periodTypeOptions: FilterOption[];
  periodError?: string;
  onChange: (patch: Partial<InstallmentsFilterValues>) => void;
  onClear: () => void;
}

export function ExtratoParcelasFilters({
  open,
  hasActiveFilter,
  userType,
  values,
  advisoryOptions,
  brokerOptions,
  productOptions,
  branchOptions,
  installmentStatusOptions,
  periodTypeOptions,
  periodError,
  onChange,
  onClear,
}: FiltersProps) {
  // Regras de perfil: seguradora vê assessoria e corretor; assessoria vê só
  // corretor; corretor não vê nenhum (escopo já vem do usuário).
  const showAdvisory = userType === 'insurer';
  const showBroker = userType === 'insurer' || userType === 'advisory';

  return (
    <div
      className={`grid transition-all duration-300 ease-in-out ${
        open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      }`}
    >
      <div className='overflow-hidden'>
        <div className='mt-3 rounded-3xl bg-[#E7E8E9]/50 px-6 py-4'>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4'>
            {showAdvisory && (
              <Field label='Assessoria'>
                <FilterCombobox
                  placeholder='Selecione a assessoria'
                  options={advisoryOptions}
                  value={values.advisoryPartyCode}
                  onChange={next => onChange({ advisoryPartyCode: next })}
                />
              </Field>
            )}

            {showBroker && (
              <Field label='Corretor'>
                <FilterCombobox
                  placeholder='Selecione o corretor'
                  options={brokerOptions}
                  value={values.brokerPartyCode}
                  onChange={next => onChange({ brokerPartyCode: next })}
                />
              </Field>
            )}

            <Field label='Produto'>
              <FilterCombobox
                placeholder='Selecione o produto'
                options={productOptions}
                value={values.productCode}
                onChange={next => onChange({ productCode: next })}
              />
            </Field>

            <Field label='Filial'>
              <FilterCombobox
                placeholder='Selecione a filial'
                options={branchOptions}
                value={values.branchCode}
                onChange={next => onChange({ branchCode: next })}
              />
            </Field>

            <Field label='Status'>
              <FilterCombobox
                placeholder='Selecione o status'
                options={installmentStatusOptions}
                value={values.installmentStatus}
                onChange={next => onChange({ installmentStatus: next })}
              />
            </Field>

            <Field label='Tipo de Período'>
              <FilterCombobox
                placeholder='Emissão / Vencimento'
                options={periodTypeOptions}
                value={values.periodType}
                onChange={next => onChange({ periodType: next })}
              />
            </Field>

            <Field label='Período'>
              <DateRangeFilterField
                from={values.periodFrom}
                to={values.periodTo}
                placeholder='Período (até 3 meses)'
                onChange={range =>
                  onChange({ periodFrom: range.from, periodTo: range.to })
                }
              />
            </Field>

            <div className='flex items-end'>
              {hasActiveFilter && (
                <button
                  type='button'
                  onClick={onClear}
                  className='flex items-center gap-1 py-2 text-sm font-semibold text-alba-blue-500 hover:text-alba-blue-600'
                >
                  <X className='h-4 w-4' />
                  Limpar Seleção
                </button>
              )}
            </div>
          </div>

          {periodError && (
            <p className='mt-3 text-sm text-alba-error-red-500'>
              {periodError}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
